import { useActionSignatureContext } from "app/features/actionSignature/providers/ActionSignatureProvider";
import { AssessmentSubmissionFormModel } from "app/features/assessments/providers/AssessmentFormProvider";
import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import {
  useSubmitFormMutation,
  useSubmitFormNewVersionMutation,
  useUpdateFormMutation,
  useUploadFormEntryFileMutation,
} from "app/services/api/formsApi";
import { FormStatusEnum } from "app/services/api/types/forms/enums/FormStatusEnums";
import {
  FILE_TYPES,
  QuestionTypeEnum,
} from "app/services/api/types/forms/enums/QuestionTypeEnum";
import { AssessmentModel } from "app/services/api/types/forms/models/AssessmentModel";
import { SubmitQuestionModel } from "app/services/api/types/forms/models/SubmitQuestionModel";
import { SubmittedFormModel } from "app/services/api/types/forms/models/SubmittedFormModel";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { ThrelsFileType, isDocumentPickerAsset } from "ui/types/ThrelsFileType";
import { isQuestionOfTypeFile } from "./useAssessmentDefaultValues";

interface Props {
  assessmentModel: AssessmentModel;
  entryData?: SubmittedFormModel;
  entriesAreVersioned?: boolean;
}

interface SaveModel {
  data: AssessmentSubmissionFormModel;
  isDraft: boolean;
}

export function useAssessmentSaving({
  assessmentModel,
  entryData,
  entriesAreVersioned,
}: Props) {
  const { resident } = useResidentContext();
  const { showActionSignatureModal } = useActionSignatureContext();

  const [triggerSubmit] = useSubmitFormMutation();
  const [triggerUpdate] = useUpdateFormMutation();
  const [triggerUpload] = useUploadFormEntryFileMutation();
  const [triggerSubmitNewVersion] = useSubmitFormNewVersionMutation();

  /**
   * Processes form submission data and separates answers from file uploads
   * @param data - The form submission data containing all question answers
   * @returns Object containing processed answers and files to be uploaded
   */
  const processAnswerData = (data: AssessmentSubmissionFormModel) => {
    const questionAnswerPairs = Object.entries(data);
    const filesToSubmit: {
      question_id: number;
      media: ThrelsFileType[];
      question_type: QuestionTypeEnum;
    }[] = [];

    const answers: SubmitQuestionModel[] = [];

    questionAnswerPairs.forEach(([question_id, answer]) => {
      // Handle file-type questions (images, documents, signatures)
      if (
        isQuestionOfTypeFile({ type: answer.question_type }) &&
        answer.files?.length
      ) {
        filesToSubmit.push({
          question_id: Number(question_id),
          question_type: answer.question_type,
          media: answer.files,
        });
      } else if (!isQuestionOfTypeFile({ type: answer.question_type })) {
        // Handle regular text-based questions.
        //
        // A scalar input left untouched holds "", which would otherwise be
        // sent as [""] and read as an answer; send [] so a blank stays
        // unambiguously blank. Array-typed questions (radio, multiselect)
        // already default to [].
        //
        // File-type questions are excluded: with no file they carry no text
        // value, and electronic signatures get their own entry below.
        const rawValue = Array.isArray(answer.value)
          ? answer.value
          : [answer.value];

        answers.push({
          question_id: Number(question_id),
          value: rawValue.filter(
            (entry) =>
              entry !== null &&
              entry !== undefined &&
              !(typeof entry === "string" && entry.trim() === ""),
          ),
          free_text: answer.free_text,
        });
      }

      // Special handling for electronic signature questions
      // They need both file upload and a "submitted" value indicator
      if (answer.question_type === QuestionTypeEnum.ELECTRONIC_SIGNATURE) {
        answers.push({
          question_id: Number(question_id),
          value: answer.files?.length ? ["submitted"] : [],
          free_text: answer.free_text,
        });
      }
    });

    return { answers, filesToSubmit };
  };

  /**
   * Determines the appropriate form status based on draft flag and assessment requirements
   * @param isDraft - Whether the form is being saved as a draft
   * @returns The appropriate FormStatusEnum value
   */
  const determineFormStatus = (isDraft: boolean): FormStatusEnum => {
    return isDraft === true
      ? FormStatusEnum.DRAFT
      : assessmentModel.requires_signoff
        ? FormStatusEnum.PENDING_SIGNOFF
        : FormStatusEnum.SUBMITTED;
  };

  /**
   * Submits form answers to the API, either creating a new entry or updating an existing one
   * @param answers - The processed question answers to submit
   * @param status - The form status (draft, pending signoff, or submitted)
   * @param password - User password for authentication
   * @param entryData - Optional existing entry data for updates
   * @returns The ID of the created or updated entry§
   */
  const submitFormAnswers = async (
    answers: SubmitQuestionModel[],
    status: FormStatusEnum,
    password: string,
    entryData?: SubmittedFormModel,
  ): Promise<number> => {
    // Update existing entry if entryData.id exists
    if (entryData?.id) {
      if (entriesAreVersioned) {
        await triggerSubmitNewVersion({
          body: { status, answers, password },
          entryId: entryData.id,
        }).unwrap();
        return entryData.id;
      } else {
        await triggerUpdate({
          body: { status, answers, password },
          entryId: entryData.id,
        }).unwrap();
        return entryData.id;
      }
    } else {
      // Create new entry
      const { entry_id } = await triggerSubmit({
        body: { status, answers, password },
        formId: assessmentModel.id,
        residentId: resident!.id,
      }).unwrap();
      return entry_id;
    }
  };

  /**
   * Uploads all files associated with the form submission
   * @param filesToSubmit - Array of files that need to be uploaded
   * @param entryId - The ID of the form entry to attach files to
   */
  const uploadFormFiles = async (
    filesToSubmit: {
      question_id: number;
      media: ThrelsFileType[];
      question_type: QuestionTypeEnum;
    }[],
    entryId: number,
  ): Promise<void> => {
    const filesBeingSubmitted: Promise<void>[] = [];

    // Process each file and create upload promises
    filesToSubmit.forEach((file) => {
      file.media.forEach((media) => {
        // Only upload if the media has an actual file (not just metadata)
        if (isDocumentPickerAsset(media) && media.file) {
          filesBeingSubmitted.push(
            triggerUpload({
              entryId,
              question: file.question_id,
              file: media.file,
              question_type: file.question_type,
            }).unwrap(),
          );
        }
      });
    });

    // Wait for all file uploads to complete
    await Promise.all(filesBeingSubmitted);
  };

  /**
   * Main save function that orchestrates the entire form submission process
   * @param data - The complete form submission data
   * @param isDraft - Whether to save as draft or submit
   */
  const save = async ({ data, isDraft }: SaveModel) => {
    // Validate that we have a resident context
    if (!resident?.id) {
      console.log("No resident provider");
      return;
    }

    try {
      // Step 1: Process and separate answers from files
      const { answers, filesToSubmit } = processAnswerData(data);

      // Step 2: Determine the appropriate form status
      const status = determineFormStatus(isDraft);

      // Step 3: Get user authentication for submission
      const resp = await showActionSignatureModal({
        title: "Submit form",
        description: "Please enter your password to submit the form",
      });

      // Step 4: Submit the form answers (create or update)
      const entryId = await submitFormAnswers(
        answers,
        status,
        resp.password,
        entryData,
      );

      // Step 5: Upload any associated files (if present)
      if (filesToSubmit.length > 0) {
        await uploadFormFiles(filesToSubmit, entryId);
      }
    } catch (e) {
      // Re-throw error to be handled by the calling component
      throw e;
    }
  };

  return {
    save,
  };
}
